// What a Scout reply carries besides its prose: the follow-up offers, and which links are safe.
// Two kinds of link survive: an absolute http(s) URL (a source, opened in a new tab) and an
// account link, `/execution?account=<id>`, which opens that account on My Accounts in the app.
//
// Scout ends a research answer with one marker line (`scout/agent/instructions.md`):
//
//   [[follow-ups: Draft a follow-up to Alex | Find their voice and support leads]]
//
// The tab shows those as chips under the reply, and a click sends the chip's text as the next
// message. The marker never shows as prose: a well-formed one becomes chips, a malformed one
// (`[[follow-ups: a | b` with no close) is dropped, and while a turn is still streaming a last
// line that is the start of a marker (`[[fol`, `[[follow-ups: Dra`) is held back. Any other `[[`
// line is the model's prose and is left alone.
//
// Pure, and imported by `node --test`, so it takes no dependencies at all.

export interface ScoutReply {
  body: string;
  followUps: string[];
}

const MAX_FOLLOW_UPS = 3;
const MAX_FOLLOW_UP_CHARACTERS = 120;
// What a marker's first characters can be, in either spelling the marker pattern accepts.
const MARKER_OPENINGS = ['[[follow-ups:', '[[followups:'];

// A whole marker line, anywhere in the reply; the last one wins.
const FOLLOW_UP_LINE = /^[ \t]*\[\[\s*follow-?ups?\s*:([^\]\n]*)\]\][ \t\r]*$/gimu;
// A line that is plainly meant as a marker but is not a well-formed one: dropped, never shown.
const MALFORMED_MARKER_LINE = /^[ \t]*\[\[\s*follow-?ups?\b[^\n]*$/gimu;

/**
 * The prose to render and the follow-ups to offer. `streaming` is true while the turn is still
 * arriving, so the start of a marker on the last line is held back rather than flashed.
 */
export function splitScoutReply(text: string, streaming = false): ScoutReply {
  // Every complete marker line comes out of the prose; the last one's items are the offers.
  const markers = [...text.matchAll(FOLLOW_UP_LINE)];
  const lastMarker = markers[markers.length - 1];
  const followUps = lastMarker ? followUpItems(lastMarker[1] ?? '') : [];
  let body = text.replace(FOLLOW_UP_LINE, '').replace(MALFORMED_MARKER_LINE, '');

  // Mid-stream only: a last line that is still becoming a marker (`[[`, `[[follow-u`).
  if (streaming) body = withoutMarkerStart(body);
  return { body: body.replace(/\n{3,}/gu, '\n\n').trim(), followUps };
}

// The text without its last line when that line is a prefix of `[[follow-ups:`.
function withoutMarkerStart(text: string): string {
  const lineStart = text.lastIndexOf('\n') + 1;
  const lastLine = text.slice(lineStart).trim().toLowerCase();
  if (lastLine.length < 2) return text;
  for (const opening of MARKER_OPENINGS) {
    if (opening.startsWith(lastLine)) return text.slice(0, lineStart);
  }
  return text;
}

// `a | b | c` → up to three short, distinct offers.
function followUpItems(items: string): string[] {
  const offers: string[] = [];
  for (const raw of items.split('|')) {
    const offer = raw.trim().replace(/\s+/gu, ' ');
    if (!offer || offer.length > MAX_FOLLOW_UP_CHARACTERS) continue;
    if (offers.some(existing => existing.toLowerCase() === offer.toLowerCase())) continue;
    offers.push(offer);
    if (offers.length === MAX_FOLLOW_UPS) break;
  }
  return offers;
}

/**
 * A link Scout wrote, if it is safe to open: an absolute http(s) URL, else null.
 * `javascript:`, `data:`, `mailto:`, relative paths and anything unparseable render as text.
 */
export function safeLinkHref(href: string | null | undefined): string | null {
  const trimmed = (href ?? '').trim();
  if (!trimmed) return null;
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
  return parsed.href;
}

// The one in-app route a reply may link to: My Accounts with one account's panel open.
const ACCOUNT_LINK_PATH = '/execution';
// A Salesforce Account id: `001`, then 12 characters, then the optional 3-character checksum.
const ACCOUNT_ID = /^001[A-Za-z0-9]{12}(?:[A-Za-z0-9]{3})?$/u;

/** The board path that opens one account's panel: `/execution?account=<id>`. */
export function accountLinkPath(accountId: string): string {
  return `${ACCOUNT_LINK_PATH}?account=${accountId}`;
}

/**
 * A link Scout wrote to an account, as an in-app path, else null. Accepted: `/execution?account=<id>`,
 * relative or absolute on this page's own `origin` (the Slack form). Nothing else in the query, no
 * fragment, and a Salesforce Account id. The path is rebuilt from the id rather than passed
 * through, so no other route, host or scheme can come out of it.
 */
export function appAccountLink(href: string | null | undefined, origin: string): string | null {
  const trimmed = (href ?? '').trim();
  if (!trimmed) return null;
  let parsed: URL;
  let pageOrigin: string;
  try {
    pageOrigin = new URL(origin).origin;
    parsed = new URL(trimmed, pageOrigin);
  } catch {
    return null;
  }

  // Same scheme and host as the page, and exactly the board's path.
  if (parsed.origin !== pageOrigin) return null;
  if (parsed.pathname !== ACCOUNT_LINK_PATH || parsed.hash) return null;

  // One query parameter, `account`, holding an Account id.
  const keys = [...parsed.searchParams.keys()];
  if (keys.length !== 1 || keys[0] !== 'account') return null;
  const accountId = parsed.searchParams.get('account') ?? '';
  if (!ACCOUNT_ID.test(accountId)) return null;
  return accountLinkPath(accountId);
}
